import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ArrowPathIcon,
  PlusIcon,
  XMarkIcon,
  PhoneIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

const SendPollPage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [chatId, setChatId] = useState('');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [allowMultipleAnswers, setAllowMultipleAnswers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getWhatsAppClients();
      setAgents(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching WhatsApp clients:', error);
      toast.error('Failed to load WhatsApp clients');
    } finally {
      setLoading(false);
    }
  };

  // Prepare WhatsApp clients data for SearchableSelect
  const agentOptions = agents && Object.keys(agents).length > 0
    ? Object.values(agents)
        .filter(client => client.status === 'active')
        .map((client) => ({
          value: client.id,
          label: client.agent_name || client.display_name || `Client ${client.client_id}`,
          description: client.whatsapp_number || client.client_id,
          icon: '📱',
          port: client.port,
          webhookApiKey: client.webhook_api_key || client.api_key,
          clientId: client.client_id
        }))
    : [];

  const addOption = () => {
    if (options.length < 12) {
      setOptions([...options, '']);
    }
  };

  const removeOption = (index) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const updateOption = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleSendPoll = async () => {
    const validOptions = options.filter(opt => opt.trim() !== '');

    if (!selectedAgent || !question || validOptions.length < 2 || !chatId) {
      toast.error('Please fill in all fields and provide at least 2 poll options');
      return;
    }

    setSending(true);
    try {
      // Find client from the agents structure
      const client = Object.values(agents).find(a => a.id === selectedAgent);
      
      if (!client) {
        toast.error('WhatsApp client not found');
        return;
      }

      // Use the new backend API endpoint that handles API key automatically
      const response = await api.sendPollMessage(
        client.client_id,
        chatId,
        {
          name: question,
          options: validOptions,
          multipleAnswers: allowMultipleAnswers
        }
      );

      toast.success('Poll sent successfully!');
      setQuestion('');
      setOptions(['', '']);
      setChatId('');
      setAllowMultipleAnswers(false);
    } catch (error) {
      console.error('Error sending poll:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.message || 'Failed to send poll');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <ChartBarIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Send Poll</h1>
                  <p className="text-sm text-gray-600">Create and send interactive polls to WhatsApp contacts</p>
                </div>
              </div>
              <button
                onClick={fetchAgents}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Poll Configuration</h2>
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSendPoll(); }} className="px-6 py-6 space-y-6">

            {/* Agent Selection */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select WhatsApp Agent
              </h3>

              <SearchableSelect
                label="WhatsApp Agent"
                name="client"
                options={agentOptions}
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                placeholder="Search and select a WhatsApp agent..."
                isRequired={true}
                isSearchable={true}
                isClearable={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">WhatsApp Agent Selection</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Choose a connected WhatsApp agent to send your poll. Only agents with active connections are available.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Poll Details */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Poll Details
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Chat ID <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="233554694531"
                    required
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Enter phone number in international format (without +)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Poll Question <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="What is your favorite color?"
                    required
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Enter a clear, engaging question for your poll
                </p>
              </div>

              {/* Poll Options */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Poll Options <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addOption}
                    disabled={options.length >= 12}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Option
                  </button>
                </div>

                <div className="space-y-3">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                      </div>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                        placeholder={`Option ${index + 1}`}
                        required
                      />
                      {options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex items-center">
                  <input
                    type="checkbox"
                    id="multipleAnswers"
                    checked={allowMultipleAnswers}
                    onChange={(e) => setAllowMultipleAnswers(e.target.checked)}
                    className="h-4 w-4 text-whatsapp-600 focus:ring-whatsapp-500 border-gray-300 rounded"
                  />
                  <label htmlFor="multipleAnswers" className="ml-2 block text-sm text-gray-700">
                    Allow multiple answers
                  </label>
                </div>

                <p className="text-sm text-gray-500 mt-2">
                  Minimum 2 options required, maximum 12 options allowed
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setSelectedAgent('');
                  setQuestion('');
                  setOptions(['', '']);
                  setChatId('');
                  setAllowMultipleAnswers(false);
                }}
                disabled={sending}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={!selectedAgent || !question || options.filter(opt => opt.trim()).length < 2 || !chatId || sending}
                className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {sending ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Creating Poll...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    Send Poll
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SendPollPage;